import { createSlice } from "@reduxjs/toolkit";
import { axiosInstance } from "../../../../Common/Interceptors/AxiosConfig";
import { showNotification } from "../../../../Common/Toast/Toast";

const initialState = {
  availability: null,
  isLoading: false,
  error: null,
  currentStatus: 'available', // available, busy, offline
  currentAvailability: null, // Current availability check result
};

const availabilitySlice = createSlice({
  name: "availability",
  initialState,
  reducers: {
    setLoading(state, action) {
      state.isLoading = action.payload;
    },
    setAvailability(state, action) {
      state.availability = action.payload;
      if (action.payload?.currentStatus) {
        state.currentStatus = action.payload.currentStatus;
      } else if (action.payload?.status) {
        state.currentStatus = action.payload.status;
      }
    },
    setCurrentStatus(state, action) {
      state.currentStatus = action.payload;
      if (state.availability) {
        state.availability.status = action.payload;
      }
    },
    setCurrentAvailability(state, action) {
      state.currentAvailability = action.payload;
    },
    setError(state, action) {
      state.error = action.payload;
    },
    clearAvailability(state) {
      state.availability = null;
      state.currentStatus = 'available';
      state.currentAvailability = null;
      state.error = null;
    },
  },
});

export const {
  setLoading,
  setAvailability,
  setCurrentStatus,
  setCurrentAvailability,
  setError,
  clearAvailability,
} = availabilitySlice.actions;

const getErrorMessage = (error, fallback = "Something went wrong") =>
  error?.response?.data?.message || error?.message || fallback;

/**
 * Fetch availability for a service
 */
export const fetchAvailability = (serviceId) => async (dispatch) => {
  if (!serviceId) {
    dispatch(setError("Service ID is required"));
    
    return;
  }

  dispatch(setLoading(true));
  dispatch(setError(null));

  try {
    const response = await axiosInstance.get(`/serviceProvider/availability/${serviceId}`);
    
    if (response.data?.status === "success") {
      const availabilityData = response.data.data?.availability || response.data.data;
      dispatch(setAvailability(availabilityData));
      if (availabilityData?.currentStatus) {
        dispatch(setCurrentStatus(availabilityData.currentStatus));
      }
    } else {
      throw new Error(response.data?.message || "Failed to fetch availability");
    }
  } catch (error) {
    
    // Even on error, set default availability so UI can still be used
    const defaultAvailability = {
      currentStatus: 'available',
      temporaryStatus: { isActive: false },
      businessHours: {
        monday: { isOpen: false, hours: [] },
        tuesday: { isOpen: false, hours: [] },
        wednesday: { isOpen: false, hours: [] },
        thursday: { isOpen: false, hours: [] },
        friday: { isOpen: false, hours: [] },
        saturday: { isOpen: false, hours: [] },
        sunday: { isOpen: false, hours: [] }
      },
      autoUpdate: { enabled: false, updateTime: "00:00", timezone: "Asia/Kolkata" },
      specialHours: []
    };
    dispatch(setAvailability(defaultAvailability));
    dispatch(setError(getErrorMessage(error)));
    showNotification({
      message: getErrorMessage(error, "Failed to fetch availability. Using defaults."),
      type: "warning",
      theme: "light",
    });
  } finally {
    dispatch(setLoading(false));
  }
};

/**
 * Update business hours
 */
export const updateBusinessHours = (serviceId, businessHours) => async (dispatch) => {
  dispatch(setLoading(true));
  dispatch(setError(null));

  try {
    const response = await axiosInstance.put(
      `/serviceProvider/availability/${serviceId}/business-hours`,
      { businessHours }
    );
    
    if (response.data?.status === "success") {
      if (response.data.data?.availability) {
        dispatch(setAvailability(response.data.data.availability));
      }
      showNotification({
        message: "Business hours updated successfully",
        type: "success",
        theme: "light",
      });
    } else {
      throw new Error(response.data?.message || "Failed to update business hours");
    }
  } catch (error) {
    dispatch(setError(getErrorMessage(error)));
    showNotification({
      message: getErrorMessage(error, "Failed to update business hours"),
      type: "error",
      theme: "light",
    });
  } finally {
    dispatch(setLoading(false));
  }
};

/**
 * Update current status (available, busy, offline)
 */
export const updateStatus = (serviceId, status) => async (dispatch) => {
  dispatch(setLoading(true));
  dispatch(setError(null));

  try {
    const response = await axiosInstance.patch(
      `/serviceProvider/availability/${serviceId}/status`,
      { status }
    );
    
    if (response.data?.status === "success") {
      dispatch(setCurrentStatus(status));
      if (response.data.data?.availability) {
        dispatch(setAvailability(response.data.data.availability));
      }
      showNotification({
        message: `Status updated to ${status}`,
        type: "success",
        theme: "light",
      });
    } else {
      throw new Error(response.data?.message || "Failed to update status");
    }
  } catch (error) {
    dispatch(setError(getErrorMessage(error)));
    showNotification({
      message: getErrorMessage(error, "Failed to update status"),
      type: "error",
      theme: "light",
    });
  } finally {
    dispatch(setLoading(false));
  }
};

/**
 * Set temporary status (busy/offline for X minutes)
 */
export const setTemporaryStatus = (serviceId, status, duration) => async (dispatch) => {
  dispatch(setLoading(true));
  dispatch(setError(null));

  try {
    const response = await axiosInstance.post(
      `/serviceProvider/availability/${serviceId}/temporary-status`,
      { status, duration }
    );
    
    if (response.data?.status === "success") {
      if (response.data.data?.availability) {
        dispatch(setAvailability(response.data.data.availability));
      }
      showNotification({
        message: `Set ${status} for ${duration} minutes`,
        type: "success",
        theme: "light",
      });
    } else {
      throw new Error(response.data?.message || "Failed to set temporary status");
    }
  } catch (error) {
    dispatch(setError(getErrorMessage(error)));
    showNotification({
      message: getErrorMessage(error, "Failed to set temporary status"),
      type: "error",
      theme: "light",
    });
  } finally {
    dispatch(setLoading(false));
  }
};

/**
 * Clear temporary status
 */
export const clearTemporaryStatus = (serviceId) => async (dispatch) => {
  dispatch(setLoading(true));
  dispatch(setError(null));

  try {
    const response = await axiosInstance.delete(
      `/serviceProvider/availability/${serviceId}/temporary-status`
    );
    
    if (response.data?.status === "success") {
      if (response.data.data?.availability) {
        dispatch(setAvailability(response.data.data.availability));
      }
      showNotification({
        message: "Temporary status cleared",
        type: "success",
        theme: "light",
      });
    } else {
      throw new Error(response.data?.message || "Failed to clear temporary status");
    }
  } catch (error) {
    dispatch(setError(getErrorMessage(error)));
    showNotification({
      message: getErrorMessage(error, "Failed to clear temporary status"),
      type: "error",
      theme: "light",
    });
  } finally {
    dispatch(setLoading(false));
  }
};

/**
 * Update auto-update settings
 */
export const updateAutoUpdate = (serviceId, settings) => async (dispatch) => {
  dispatch(setLoading(true));
  dispatch(setError(null));

  try {
    const response = await axiosInstance.put(
      `/serviceProvider/availability/${serviceId}/auto-update`,
      settings
    );
    
    if (response.data?.status === "success") {
      if (response.data.data?.availability) {
        dispatch(setAvailability(response.data.data.availability));
      }
      showNotification({
        message: "Auto-update settings updated",
        type: "success",
        theme: "light",
      });
    } else {
      throw new Error(response.data?.message || "Failed to update auto-update settings");
    }
  } catch (error) {
    dispatch(setError(getErrorMessage(error)));
    showNotification({
      message: getErrorMessage(error, "Failed to update auto-update settings"),
      type: "error",
      theme: "light",
    });
  } finally {
    dispatch(setLoading(false));
  }
};

/**
 * Set quick schedule (Monday-Friday 9-5)
 */
export const setQuickSchedule = (serviceId, options = {}) => async (dispatch) => {
  dispatch(setLoading(true));
  dispatch(setError(null));

  try {
    const { startTime = "09:00", endTime = "17:00", workingDays = ["monday", "tuesday", "wednesday", "thursday", "friday"] } = options;
    
    const response = await axiosInstance.post(
      `/serviceProvider/availability/${serviceId}/quick-schedule`,
      { startTime, endTime, workingDays }
    );
    
    if (response.data?.status === "success") {
      if (response.data.data?.availability) {
        dispatch(setAvailability(response.data.data.availability));
      }
      showNotification({
        message: "Quick schedule set successfully",
        type: "success",
        theme: "light",
      });
    } else {
      throw new Error(response.data?.message || "Failed to set quick schedule");
    }
  } catch (error) {
    dispatch(setError(getErrorMessage(error)));
    showNotification({
      message: getErrorMessage(error, "Failed to set quick schedule"),
      type: "error",
      theme: "light",
    });
  } finally {
    dispatch(setLoading(false));
  }
};

/**
 * Simplified unified availability setup
 * Supports templates: 'weekdays', 'everyday', 'weekends', 'monday-friday'
 * Or custom template with { startTime, endTime, days }
 */
export const setupAvailability = (serviceId, config) => async (dispatch) => {
  dispatch(setLoading(true));
  dispatch(setError(null));

  try {
    const response = await axiosInstance.post(
      `/serviceProvider/availability/${serviceId}/setup`,
      config
    );
    
    if (response.data?.status === "success") {
      if (response.data.data?.availability) {
        dispatch(setAvailability(response.data.data.availability));
      }
      showNotification({
        message: "Availability set successfully",
        type: "success",
        theme: "light",
      });
    } else {
      throw new Error(response.data?.message || "Failed to set availability");
    }
  } catch (error) {
    dispatch(setError(getErrorMessage(error)));
    showNotification({
      message: getErrorMessage(error, "Failed to set availability"),
      type: "error",
      theme: "light",
    });
  } finally {
    dispatch(setLoading(false));
  }
};

/**
 * Check current availability
 */
export const checkCurrentAvailability = (serviceId) => async (dispatch) => {
  if (!serviceId) {
    
    return;
  }

  try {
    const response = await axiosInstance.get(
      `/serviceProvider/availability/${serviceId}/current`
    );
    
    if (response.data?.status === "success") {
      dispatch(setCurrentAvailability(response.data.data));
    }
  } catch (error) {
    
  }
};

export default availabilitySlice.reducer;

